import Page from '../../../../components/PageWrapper';
import { useState } from 'react';
import GTable from '../../../../components/elements/GTable';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ITableAction, ITablePaginationData } from '../../../../components/elements/GTable/GTable';
import useModal from '../../../../hooks/modalHook';
import { capitalizeSentence } from '../../../../utils/miscFunctions';
import useRoute from '../../../../hooks/routesHook';
import { getAllTransactions } from '../../../../services/transactions';
import { ITransaction } from '../../../../resources/interfaces';
import Transaction from '../../../../models/Transaction';
import useTransactions from '../../../../hooks/transactionsHook';
import { Eye } from 'lucide-react';


const Transactions = (): JSX.Element => {
    const page = 'transaction';
    const [transactions, setTransactions] = useState<any[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
    const [loading, setLoading] = useState<boolean>(true);
    const [transactionsPaginationData, setTransactionsPaginationData] = useState<ITablePaginationData>();
    const [showPerPage, setShowPerPage] = useState<number>(100);
    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const navigate = useNavigate();
    const { getDashboardBase } = useRoute();
    const invalidateQueries = useQueryClient();

    const getTableRowActions = (transaction: ITransaction) => {
        const tableActions: ITableAction[] = [
            {
                label: 'View',
                icon: <Eye className = "h-4 w-4" />,
                callback: () => navigate(`${getDashboardBase()}/transactions/${transaction?.id}`)
            }
        ];

        return tableActions;
    }

    const { columns } = useTransactions({ tableRowActions: getTableRowActions });

    useQuery({
        queryKey: ['fetchAllTransactions', currentPage, showPerPage],
        queryFn: () => fetchAllTransactions()
    });

    const fetchAllTransactions = async () => {
        try {
            const response: any = await getAllTransactions(currentPage, showPerPage);

            if (response) {
                const _transactions = response?.content.map((_transaction: ITransaction) => new Transaction(_transaction));
                setTransactions(_transactions);

                const from = response.pageable?.offset + 1;
                const to =
                    from + response.size > response.totalElements
                        ? response.totalElements
                        : from + response.size - 1;

                setTransactionsPaginationData({
                    ...transactionsPaginationData,
                    per_page: response.size ?? 0,
                    total: response.totalElements ?? 0,
                    totalPages: response.totalPages ?? 0,
                    from,
                    to
                });
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }


    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setSearchParams({ page: String(page) });
    };

    return (
        <Page title={`${capitalizeSentence(page)}`}>
            <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg shadow-md">
                <div className="">
                    <div className="inline-flex items-center gap-2 mb-2">
                        <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                        <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">{capitalizeSentence(page)}s</h2>
                    </div>
                    
                    <p className="text-sm text-slate-400 dark:text-darkBg-subText">
                        View a list of all {page}s and their details
                    </p>
                </div>

                <div className="overflow-x-auto mt-4">
                    <GTable
                        columns={columns}
                        loading={loading}
                        data={transactions}
                        classes="h-fit"
                        showSearch={false}
                        tableType={'transaction'}
                        paginationData={transactionsPaginationData}
                        onPageChange={handlePageChange}
                        showPerPage={showPerPage}
                        stickyHeader
                        onShowPerPageChange={(page: number) => setShowPerPage(page)}
                        onRowClick={(row: any) => navigate(`${getDashboardBase()}/transactions/${row.id}`)}
                    />
                </div>
            </div>
            <ModalComponent />
        </Page>
    );
};

export default Transactions;
